import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/f5502c8213cacad10181856e9050c60c/ed731c15cb79f79d6f6f2b2cd62b80e9.png" 
                alt="Abu Ayaan Services Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Abu Ayaan Services WLL</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#about" className={`text-sm font-medium hover:text-teal-500 transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700' : 'text-white'}`}>About Us</a>
              <a href="#services" className={`text-sm font-medium hover:text-teal-500 transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Services</a>
              <a href="#mission" className={`text-sm font-medium hover:text-teal-500 transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Mission & Vision</a>
              <a href="#contact" className={`text-sm font-medium hover:text-teal-500 transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
              <a href="tel:+97336096336" className="bg-teal-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap">
                Call Now
              </a>
            </div>

            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden"
            >
              <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 bg-white rounded-lg shadow-lg">
              <div className="flex flex-col gap-4 p-4">
                <a href="#about" className="text-sm font-medium text-gray-700 hover:text-teal-500">About Us</a>
                <a href="#services" className="text-sm font-medium text-gray-700 hover:text-teal-500">Services</a>
                <a href="#mission" className="text-sm font-medium text-gray-700 hover:text-teal-500">Mission & Vision</a>
                <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-teal-500">Contact</a>
                <a href="tel:+97336096336" className="bg-teal-500 text-white px-6 py-2.5 rounded-full text-sm font-medium text-center">
                  Call Now
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e7131271298b665d96e3fde02e31a9e7.jpg"
            alt="Abu Ayaan Services Office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Abu Ayaan Services
          </h1>
          <p className="text-2xl md:text-3xl text-white/90 mb-4 font-serif italic">
            Professional Cleaning & Staff Deployment Solutions in Bahrain
          </p>
          <p className="text-lg text-white/80 mb-12 font-light">
            Deploying Skilled Staff at Your Premises - Helpers, Office Boys & Cleaners on Contract Basis
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="#contact" 
              className="bg-teal-500 text-white px-8 py-4 rounded-full text-base font-medium hover:bg-teal-600 transition-all flex items-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              Get a Quote
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="tel:+97336096336" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-medium hover:bg-white hover:text-teal-500 transition-all flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              Call +973 36096336
            </a>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-24 bg-gradient-to-b from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-teal-500 font-semibold text-sm uppercase tracking-wider mb-4">WHO WE ARE</p>
              <h2 className="text-5xl font-bold mb-6 leading-tight">
                Building Trust Through <span className="text-teal-500 font-serif italic">Excellence & Dedication</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  Abu Ayaan Services is a premier cleaning and staff deployment company in the Kingdom of Bahrain. We specialize in deploying skilled professionals directly at client premises according to their specific requirements. We attribute high levels of customer satisfaction to our unsurpassed implementation of individual attention, selective service, and our non-complacent workmanship and staff supplied and applied to each task.
                </p>
                <p>
                  Our contract-based model allows you to have dedicated staff members working at your location - whether you need helpers, office boys, or cleaners. We provide flexible daily and yearly contracts tailored to your business needs.
                </p>
                <p>
                  The company's strategic partnership in the area of Cleaning, Staff Deployment, Assistance along with Transport & Accommodation services for clients is based on its acute understanding of the opportunities which exist within these critical sectors.
                </p>
                <p>
                  Abu Ayaan Services aims to build fair, equitable and sustainable beneficial trade platforms that will contribute to effective and sustainable beneficiation of resources. The better the quality of the support services, higher will be the business growth.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-3xl overflow-hidden shadow-lg h-80">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20cleaning%20team%20in%20blue%20uniforms%20standing%20together%20in%20modern%20office%20building%20confident%20and%20friendly%20staff%20members%20diverse%20team%20with%20cleaning%20equipment%20bright%20professional%20corporate%20setting&width=800&height=600&seq=about-team-001&orientation=landscape"
                  alt="Our Professional Team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-3xl overflow-hidden shadow-lg h-52">
                <img 
                  src="https://readdy.ai/api/search-image?query=pristine%20clean%20modern%20commercial%20office%20space%20with%20glass%20windows%20polished%20floors%20bright%20natural%20lighting%20empty%20and%20spotless%20professional%20environment%20after%20cleaning%20service&width=800&height=400&seq=about-facility-001&orientation=landscape"
                  alt="Clean Facility"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start mb-16">
            <h2 className="text-5xl font-bold mb-6 md:mb-0">
              Our <span className="text-gray-800 font-serif italic">Services</span>
            </h2>
            <p className="text-gray-600 max-w-xl text-base leading-relaxed">
              We cater to small and big firms, as well as various departments of Bahrain Ministry, Factories, Malls and hospitals. Our services are available on a contract basis, so you can choose the duration that best suits your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-brush-line text-white text-5xl"></i>
              </div>
              <div className="inline-block bg-teal-50 text-teal-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
                CLEANING SERVICES
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">General Cleaning Services</h3>
              <ul className="space-y-3 text-gray-600 mb-6 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Deep Cleaning</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>General Cleaning</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Office Cleaning</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Villa & Apartment Cleaning</span>
                </li>
              </ul>
              <a href="#contact" className="text-teal-500 font-medium flex items-center gap-2 hover:gap-3 transition-all text-sm cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-bug-line text-white text-5xl"></i>
              </div>
              <div className="inline-block bg-teal-50 text-teal-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
                SPECIALIZED SERVICES
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Specialized Services</h3>
              <ul className="space-y-3 text-gray-600 mb-6 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Pest Control</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Loading & Unloading</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Tower Glass Cleaning</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Sofa & Carpet Shampooing</span>
                </li>
              </ul>
              <a href="#contact" className="text-teal-500 font-medium flex items-center gap-2 hover:gap-3 transition-all text-sm cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-32 h-32 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-white text-5xl"></i>
              </div>
              <div className="inline-block bg-teal-50 text-teal-600 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
                STAFF DEPLOYMENT
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Staff Deployment Services</h3>
              <ul className="space-y-3 text-gray-600 mb-6 text-sm">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>General Workers & Helpers</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Office Boy Service</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Dedicated Cleaners</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-500 mt-0.5"></i>
                  <span>Experienced Team</span>
                </li>
              </ul>
              <a href="#contact" className="text-teal-500 font-medium flex items-center gap-2 hover:gap-3 transition-all text-sm cursor-pointer">
                Learn More <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Staff Deployment & Cleaning Solutions</h2>
            <p className="text-xl text-teal-500 font-medium">Skilled Staff Deployed at Your Premises - Daily & Yearly Contract Basis</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Staff Deployment Section */}
            <div className="bg-white rounded-3xl p-10 shadow-xl">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-team-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Staff Deployment Services</h3>
              <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                We deploy skilled and experienced staff directly at your premises according to your specific requirements. Our professionals work as part of your team on a contract basis.
              </p>
              
              <div className="space-y-5">
                <div className="flex items-start gap-4 pb-5 border-b border-gray-200">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-star-line text-teal-500 text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-base mb-1">General Workers & Helpers</h4>
                    <p className="text-gray-600 text-sm">Skilled workers deployed at your location for various tasks</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pb-5 border-b border-gray-200">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-briefcase-line text-teal-500 text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-base mb-1">Office Boy Service</h4>
                    <p className="text-gray-600 text-sm">Professional office support staff for your workplace</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pb-5 border-b border-gray-200">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-settings-line text-teal-500 text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-base mb-1">Dedicated Cleaners</h4>
                    <p className="text-gray-600 text-sm">Experienced cleaning staff stationed at your premises</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-teal-500 text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-base mb-1">Flexible Contracts</h4>
                    <p className="text-gray-600 text-sm">Daily and yearly contract options to suit your needs</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cleaning Services Section */}
            <div className="bg-white rounded-3xl p-10 shadow-xl">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mb-6">
                <i className="ri-brush-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Professional Cleaning Services</h3>
              <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                Comprehensive cleaning solutions for all types of facilities with modern equipment and eco-friendly products.
              </p>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Public & Private Utility Cleaning Services</h4>
                  <p className="text-gray-600 text-sm">Comprehensive cleaning solutions for all types of facilities</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">General Cleaning of Buildings & Apartments</h4>
                  <p className="text-gray-600 text-sm">Regular maintenance and deep cleaning for residential and commercial properties</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Restroom Sanitization</h4>
                  <p className="text-gray-600 text-sm">Complete sanitization and hygiene maintenance for restrooms</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Floor Scrubbing & Polishing</h4>
                  <p className="text-gray-600 text-sm">Professional floor care with modern equipment and techniques</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Tower Glass Cleaning</h4>
                  <p className="text-gray-600 text-sm">Specialized high-rise window and glass facade cleaning services</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Sofa & Carpet Shampooing</h4>
                  <p className="text-gray-600 text-sm">Deep cleaning and stain removal for upholstery and carpets</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Hi-Tech Deep Cleaning</h4>
                  <p className="text-gray-600 text-sm">Advanced cleaning technology for thorough sanitization</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Complex Cleaning</h4>
                  <p className="text-gray-600 text-sm">Comprehensive cleaning solutions for large complexes and facilities</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg mb-1">Post-Construction Cleaning</h4>
                  <p className="text-gray-600 text-sm">Thorough cleanup after construction or renovation projects</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contract Benefits Section */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Choose Contract-Based Staff Deployment?</h2>
                <p className="text-xl text-gray-600">Flexible, Cost-Effective, and Reliable</p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-money-dollar-circle-line text-teal-500 text-4xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Cost-Effective</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    No recruitment costs, training expenses, or employee benefits. Pay only for the services you need.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-time-line text-teal-500 text-4xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Flexible Duration</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Choose daily or yearly contracts based on your requirements. Scale up or down as needed.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-shield-check-line text-teal-500 text-4xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Trained Professionals</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    All staff are pre-screened, trained, and experienced. We handle all HR and management aspects.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-user-follow-line text-teal-500 text-4xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">On-Site Presence</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Staff work directly at your premises, becoming familiar with your specific needs and environment.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-customer-service-2-line text-teal-500 text-4xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Our management team ensures quality service and handles any staff-related issues promptly.
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-refresh-line text-teal-500 text-4xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Easy Replacement</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    If a staff member is unavailable, we provide immediate replacement to ensure continuity.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* Mission, Vision & Values Section */}
      <section id="mission" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-20 w-96 h-96 bg-teal-100 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-orange-100 rounded-full opacity-20 blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Mission Card */}
            <div className="bg-teal-50 rounded-2xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-target-line text-gray-900 text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Mission</h3>
              <div className="w-10 h-0.5 bg-teal-500 mx-auto mb-6"></div>
              <p className="text-gray-600 leading-relaxed text-sm">
                To deliver exceptional cleaning and contracting services that exceed customer expectations through individual attention, selective service, and dedicated workmanship. We strive to maintain the highest standards of quality and professionalism in every task we undertake.
              </p>
            </div>

            {/* Vision Card */}
            <div className="bg-teal-50 rounded-2xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-eye-line text-gray-900 text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Vision</h3>
              <div className="w-10 h-0.5 bg-teal-500 mx-auto mb-6"></div>
              <p className="text-gray-600 leading-relaxed text-sm">
                To be the leading cleaning service provider in the Kingdom of Bahrain, recognized for our excellence, reliability, and commitment to sustainable practices. We aim to set industry standards and build long-lasting partnerships with our clients.
              </p>
            </div>

            {/* Values Card */}
            <div className="bg-teal-50 rounded-2xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-heart-line text-gray-900 text-6xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Values</h3>
              <div className="w-10 h-0.5 bg-teal-500 mx-auto mb-6"></div>
              <p className="text-gray-600 leading-relaxed text-sm">
                Professionalism, reliability, sustainability, and excellence guide everything we do. We believe in fair and equitable trade practices, continuous improvement, and building trust through consistent quality service delivery and customer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Trust Section */}
      <section className="py-24 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Trusted by Leading Organizations</h2>
            <p className="text-xl text-gray-600">Serving Bahrain's Top Institutions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-line text-teal-500 text-5xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 text-base">Factories</h4>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-store-line text-teal-500 text-5xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 text-base">Shopping Malls</h4>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg transition-shadow text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-hospital-line text-teal-500 text-5xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 text-base">Hospitals</h4>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">Ready to Experience Professional Cleaning?</h2>
          <p className="text-xl text-gray-600 mb-8">Contact us today for a free consultation and quote</p>
          <a 
            href="tel:+97336096336" 
            className="inline-flex items-center gap-3 bg-teal-500 text-white px-10 py-5 rounded-full text-lg font-medium hover:bg-teal-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            <i className="ri-phone-line text-2xl"></i>
            Call +973 36096336
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="mt-6 text-gray-600">
            <a href="#contact-form" className="text-teal-500 hover:text-teal-600 font-medium inline-flex items-center gap-1">
              Or send us a message <i className="ri-arrow-right-line"></i>
            </a>
          </p>
        </div>

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto px-6">
          <form 
            id="contact-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d4pjrvqr20r5k3vfkjg0"
            method="POST"
            className="bg-gray-50 rounded-3xl p-10 shadow-lg"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validate
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const phone = formData.get('phone') as string;
              const message = formData.get('message') as string;

              if (!name || !email || !phone || !message) {
                alert('Please fill in all fields');
                return;
              }

              if (message.length > 500) {
                alert('Message must be 500 characters or less');
                return;
              }

              // Submit
              fetch('https://readdy.ai/api/form/d4pjrvqr20r5k3vfkjg0', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData as any).toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('Thank you! We will contact you soon.');
                  form.reset();
                } else {
                  alert('Something went wrong. Please try again.');
                }
              })
              .catch(() => {
                alert('Something went wrong. Please try again.');
              });
            }}
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Send Us a Message</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input 
                  type="text" 
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input 
                  type="email" 
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input 
                type="tel" 
                id="phone"
                name="phone"
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="+973 XXXX XXXX"
              />
            </div>

            <div className="mb-6">
              <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
              <select 
                id="service"
                name="service"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              >
                <option value="General Cleaning">General Cleaning</option>
                <option value="Deep Cleaning">Deep Cleaning</option>
                <option value="Pest Control">Pest Control</option>
                <option value="Office Services">Office Services</option>
                <option value="Specialized Cleaning">Specialized Cleaning</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message (Max 500 characters)</label>
              <textarea 
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell us about your cleaning needs..."
              ></textarea>
            </div>

            <button 
              type="submit"
              className="w-full bg-teal-500 text-white py-4 rounded-lg font-medium hover:bg-teal-600 transition-colors text-base whitespace-nowrap"
            >
              Send Message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-20 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            {/* Column 1 */}
            <div>
              <img 
                src="https://static.readdy.ai/image/f5502c8213cacad10181856e9050c60c/ed731c15cb79f79d6f6f2b2cd62b80e9.png" 
                alt="Abu Ayaan Services" 
                className="h-12 mb-4 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional cleaning and contracting services in Bahrain. Trusted by ministries, factories, malls, and hospitals.
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h4 className="text-sm font-semibold uppercase text-gray-400 mb-6 tracking-wider">QUICK CONTACT</h4>
              <div className="space-y-4">
                <a href="tel:+97336096336" className="flex items-center gap-3 text-white hover:text-teal-400 transition-colors text-sm">
                  <i className="ri-phone-line"></i>
                  <span>+973 36096336</span>
                </a>
                <div className="flex items-center gap-3 text-white text-sm">
                  <i className="ri-map-pin-line"></i>
                  <span>Kingdom of Bahrain</span>
                </div>
              </div>
            </div>

            {/* Column 3 */}
            <div>
              <h4 className="text-sm font-semibold uppercase text-gray-400 mb-6 tracking-wider">SERVICES</h4>
              <ul className="space-y-4">
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm">General Cleaning</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm">Deep Cleaning</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm">Pest Control</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm">Office Services</a></li>
                <li><a href="#services" className="text-gray-300 hover:text-white transition-colors text-sm">Specialized Cleaning</a></li>
              </ul>
            </div>

            {/* Column 4 */}
            <div>
              <h4 className="text-sm font-semibold uppercase text-gray-400 mb-6 tracking-wider">FOLLOW US</h4>
              <ul className="space-y-4">
                <li>
                  <a href="https://wa.me/97336096336" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2 text-sm">
                    <i className="ri-whatsapp-line text-lg"></i>
                    WhatsApp
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2 text-sm">
                    <i className="ri-facebook-line text-lg"></i>
                    Facebook
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-300 hover:text-white transition-colors flex items-center gap-2 text-sm">
                    <i className="ri-instagram-line text-lg"></i>
                    Instagram
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-800 pt-12">
            <div className="text-center mb-8">
              <h2 className="text-7xl font-bold text-amber-100/20 tracking-tight">ABU AYAAN SERVICES WLL</h2>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
              <p>© 2025 Abu Ayaan Services. All rights reserved.</p>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
